import {setCssVariable} from '../utils/cssVariables.js';
import {detectAncestorBackground} from '../utils/dom.js';

/**
 * @typedef {object} DraggableCarouselOptions
 * @property {string} itemSelector - CSS selector for carousel items (required)
 * @property {boolean} [autoplay=true] - Enable automatic scrolling
 * @property {number} [speed=1] - Autoplay speed as a factor of the base speed (30 px/s)
 * @property {boolean} [reverse=false] - Reverse the scroll direction
 * @property {boolean} [pauseOnHover=true] - Pause autoplay on mouse hover
 * @property {number} [releaseStiffness=20] - Spring stiffness for drag release animation
 * @property {number} [velocityMultiplier=2] - Multiplier for drag velocity on release
 * @property {boolean} [lazy=true] - Initialize only when entering viewport
 * @property {boolean} [draggable=true] - Enable drag interaction. When both draggable and pauseOnHover are false, all interactions are disabled (animation-only mode)
 * @property {number} [startOffsetPercent=0] - Initial scroll position as a fraction of the track's total width. Negative values shift the view forward (via the infinite-wrap) so partial items appear at both edges on first paint — useful for staggered/brick layouts that would otherwise show a blank strip on one side
 */

/** @type {Promise<typeof import('../modules/draggableCarousel.js')>|null} */
let modulePromise = null;

/** @type {WeakMap<HTMLElement, object>} */
const initializations = new WeakMap();
const observed = new WeakSet();
/** @type {IntersectionObserver|null} */
let observer = null;

/**
 * Behavior to initialize the DraggableCarousel module on DOM elements.
 *
 * Steady autoplay uses a Web Animation. The module handles touch drag, mouse drag,
 * horizontal wheel input, momentum, and the infinite loop.
 *
 * Data attributes (take precedence over options parameter):
 * - data-draggable-carousel: Marks the container element
 * - data-autoplay: 'true' or 'false' to enable automatic scrolling
 * - data-speed: Autoplay speed as a factor of the base speed (default: 1)
 * - data-reverse: 'true' or 'false' to reverse scroll direction
 * - data-pause-on-hover: 'true' or 'false' to pause on hover
 * - data-release-stiffness: Spring stiffness for drag release
 * - data-velocity-multiplier: Multiplier for drag velocity
 * - data-lazy: 'true' or 'false' to enable lazy initialization
 * - data-draggable: 'true' or 'false' to enable drag interaction
 * - data-infinite: 'true' or 'false' to enable seamless infinite looping
 * - data-start-offset-percent: Numeric fraction of totalWidth for the initial scroll position (e.g. -0.5 to start half-way in; ignored when data-infinite is 'false')
 *
 * @example
 *
 * <div data-draggable-carousel
 *      data-autoplay="true"
 *      data-speed="2"
 *      data-reverse="false">
 *   <div data-track>
 *     <div data-draggable-carousel-item>Item 1</div>
 *     <div data-draggable-carousel-item>Item 2</div>
 *   </div>
 * </div>
 *
 * @param {HTMLElement|null} container - The carousel container element
 * @param {Partial<DraggableCarouselOptions>} [options] - Configuration options (fallback when data attributes not present)
 * @returns {Promise<import('../modules/draggableCarousel.js').DraggableCarousel|undefined>} The carousel instance or undefined if initialization failed
 */
export async function draggableCarousel(container, options = {}) {
    if (!container) return;

    // Skip if already initialized
    if (container.carouselInstance) {
        return container.carouselInstance;
    }

    const parseBoolean = function(value) {
        if (typeof value === 'string') {
            return value.toLowerCase() === 'true';
        }
        return Boolean(value);
    };

    // Extract configuration from data attributes or use provided options/defaults
    const config = {
        itemSelector: '[data-draggable-carousel-item]',
        autoplay: parseBoolean(container.dataset.autoplay ?? options.autoplay ?? true),
        speed: parseFloat(container.dataset.speed) || options.speed || 1,
        reverse: parseBoolean(container.dataset.reverse ?? options.reverse ?? false),
        pauseOnHover: parseBoolean(container.dataset.pauseOnHover ?? options.pauseOnHover ?? true),
        releaseStiffness: parseFloat(container.dataset.releaseStiffness) || options.releaseStiffness || 20,
        velocityMultiplier: parseFloat(container.dataset.velocityMultiplier) || options.velocityMultiplier || 2,
        lazy: parseBoolean(container.dataset.lazy ?? options.lazy ?? true),
        draggable: parseBoolean(container.dataset.draggable ?? options.draggable ?? true),
        infinite: parseBoolean(container.dataset.infinite ?? options.infinite ?? true),
        // `startOffsetPercent` is an opt-in numeric (not boolean). parseFloat returns NaN for missing/empty
        // attributes, which the module clamps back to 0 via Number.isFinite, so no explicit fallback here.
        startOffsetPercent: parseFloat(container.dataset.startOffsetPercent ?? options.startOffsetPercent),
        onPositionChange: options.onPositionChange ?? null,
    };

    const initialization = initializations.get(container) ?? {};
    initializations.set(container, initialization);

    modulePromise ??= import('../modules/draggableCarousel.js').catch((error) => {
        modulePromise = null;
        throw error;
    });
    const {DraggableCarousel} = await modulePromise;
    if (initializations.get(container) !== initialization || !container.isConnected) {
        return;
    }
    if (container.carouselInstance) {
        return container.carouselInstance;
    }

    // Initialize the carousel instance (lazy logic is handled inside the module)
    const instance = new DraggableCarousel(container, config);
    instance.init();

    // Store instance for potential cleanup or programmatic control
    container.carouselInstance = instance;

    // Auto-detect parent section background for fade overlays
    const wrapper = container.parentElement;
    if (wrapper) {
        const bg = detectAncestorBackground(wrapper);
        if (bg) {
            setCssVariable('--fade-overlay-bg', bg, wrapper);
        }
    }

    return instance;
}

/**
 * Lazily initializes draggable carousels when they enter the viewport.
 */
function observeCarousels() {
    const carousels = document.querySelectorAll('[data-draggable-carousel]');

    if (carousels.length === 0) {
        return;
    }

    observer ??= new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting && observed.has(entry.target)) {
                observed.delete(entry.target);
                observer.unobserve(entry.target);
                draggableCarousel(/** @type {HTMLElement} */ (entry.target));
            }
        }
    }, {rootMargin: '200px 0px'});

    for (const carousel of carousels) {
        if (!carousel.carouselInstance && !observed.has(carousel)) {
            observed.add(carousel);
            observer.observe(carousel);
        }
    }
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', observeCarousels)
    : observeCarousels();

document.addEventListener('htmx:load', observeCarousels);
document.addEventListener('htmx:beforeCleanupElement', (event) => {
    const carousel = event.target;
    if (carousel instanceof HTMLElement && carousel.matches('[data-draggable-carousel]')) {
        observed.delete(carousel);
        observer?.unobserve(carousel);
        initializations.delete(carousel);
        carousel.carouselInstance?.destroy();
    }
});
